import React, { useState } from "react";
import { twMerge } from "tailwind-merge";
import { FormLabel, FormHelperText, formStyles } from "./FormUtils";

const Input = ({
  // Basic Props
  id,
  name,
  label = "",
  placeholder,
  validateTodayAndPast = false,
  type = "text",
  value,
  validateRange = null,
  defaultValue,
  onChange,
  onBlur,
  onFocus,
  onClick,

  // Validation Props
  isRequired = false,
  error,
  pattern,
  min,
  max,
  minLength,
  maxLength,
  step,
  errorMessage,
  // Style Props
  className = "",
  labelStyle = "",
  inputStyle = "",
  outerContainerStyle = "",
  helpTextStyle = "",
  errorTextStyle = "",
  placeholderStyle = "",
  // Error Container Props
  errorContainerStyle = "",
  // Icon Props
  icon,
  prefixIcon,
  searchIcon = false,

  // Helper Props
  helpText,
  disabled = false,
  readOnly = false,
  autoComplete,
  autoFocus = false,

  // Special Flags
  allowFillColor = false,
  isFilterSearch = false,

  // Textarea Props
  rows = 4,
  cols,
  resize = "none",

  // Input HTML attributes
  list,
  accept,
  multiple,
  isLocalUTC = false,
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const getBorderClasses = () => {
    const hasErr = !!(error || errorMessage);
    if (hasErr) return "border border-[#FF0000] dark:border-[#FF0000]";
    if (isFocused) return "border border-[#7AA7FF]";
    return "border-[#12121257] dark:border-white/20";
  };

  const handleFocus = (e) => {
    setIsFocused(true);
    if (onFocus) onFocus(e);
  };

  const handleBlur = (e) => {
    setIsFocused(false);

    if (onBlur) onBlur(e);

    if (validateRange && type === "number") {
      const val = e.target.value;
      const num = Number(val);

      if (
        !val ||
        isNaN(num) ||
        (validateRange.isInteger && !Number.isInteger(num)) ||
        (validateRange.min !== undefined && num < validateRange.min) ||
        (validateRange.max !== undefined && num > validateRange.max)
      ) {
        if (rest.onValidationFail) {
          rest.onValidationFail(
            `Please enter an integer between ${validateRange.min} and ${validateRange.max}`
          );
        }
      }
    }
  };

  const generateYearOptions = () => {
    const options = new Set();
    const currentYear = new Date().getFullYear();

    for (let i = 0; i < 10; i++) {
      options.add(currentYear - i);
    }

    if (value && !options.has(parseInt(value))) {
      options.add(parseInt(value));
    }

    return Array.from(options).sort((a, b) => b - a); // descending
  };

  const yearOptions = generateYearOptions();

  const handleClick = (e) => {
    if (onClick) onClick(e);
  };

  const baseClasses =
    type === "textarea"
      ? twMerge(formStyles.textarea, getBorderClasses(), inputStyle)
      : twMerge(formStyles.input, getBorderClasses(), inputStyle);

  const getTodayDate = (localUTC = false) => {
    const today = new Date();
    if (localUTC) {
      return new Date(
        Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())
      )
        .toISOString()
        .split("T")[0];
    }
    return today.toISOString().split("T")[0];
  };

  // Helper function to format date value for date inputs
  const formatDateValue = (dateValue) => {
    if (!dateValue) return "";

    // If it's already in yyyy-MM-dd format, return as is
    if (
      typeof dateValue === "string" &&
      /^\d{4}-\d{2}-\d{2}$/.test(dateValue)
    ) {
      return dateValue;
    }

    // If it's an ISO string or Date object, convert to yyyy-MM-dd
    try {
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split("T")[0];
      }
    } catch (e) {
      console.warn("Invalid date value:", dateValue);
    }

    return "";
  };

  // Get the properly formatted value for date inputs
  const getInputValue = () => {
    if (type === "date") {
      if (value) {
        return formatDateValue(value);
      }
      if (validateTodayAndPast) {
        return getTodayDate(isLocalUTC);
      }
      return "";
    }
    return value || "";
  };

  if (type === "textarea") {
    return (
      <div className={twMerge("flex flex-col w-full", outerContainerStyle)}>
        <div className="flex items-center gap-2">
          <FormLabel
            htmlFor={id}
            label={label}
            isRequired={isRequired}
            className={labelStyle}
            helpText={helpText}
          />
        </div>

        {helpText && (
          <span className={twMerge(formStyles.helpText, helpTextStyle)}>
            {helpText}
          </span>
        )}

        <textarea
          id={id}
          name={name}
          placeholder={placeholder}
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          disabled={disabled}
          readOnly={readOnly}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onClick={handleClick}
          autoFocus={autoFocus}
          rows={rows}
          cols={cols}
          maxLength={maxLength}
          minLength={minLength}
          autoComplete={autoComplete}
          style={{ resize }}
          className={twMerge(
            baseClasses,
            searchIcon ? "pl-10" : "",
            prefixIcon ? "pl-12" : "",
            className
          )}
          {...rest}
        />

        <FormHelperText
          error={errorMessage}
          errorClassName={formStyles.errorText}
        />
      </div>
    );
  }

  return (
    <div className={twMerge("flex flex-col w-full", outerContainerStyle)}>
      <div className="flex items-center gap-2">
        <FormLabel
          htmlFor={id}
          label={label}
          isRequired={isRequired}
          className={labelStyle}
          helpText={helpText}
        />
      </div>
      {helpText && (
        <span className={twMerge(formStyles.helpText, helpTextStyle)}>
          {helpText}
        </span>
      )}
      <div className="relative">
        {searchIcon && (
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <svg
              className="w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        )}

        {prefixIcon && (
          <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
            {prefixIcon}
          </div>
        )}

        <input
          id={id}
          type={type}
          name={name}
          placeholder={placeholder}
          value={getInputValue()}
          defaultValue={defaultValue}
          onChange={onChange}
          disabled={disabled}
          readOnly={readOnly}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onClick={handleClick}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          maxLength={maxLength}
          minLength={minLength}
          min={validateTodayAndPast && type === "date" ? undefined : min}
          max={
            validateTodayAndPast && type === "date"
              ? getTodayDate(isLocalUTC)
              : max
          }
          step={step}
          pattern={pattern}
          list={list}
          accept={accept}
          multiple={multiple}
          className={twMerge(
            baseClasses,
            placeholderStyle,
            searchIcon ? "pl-10" : "",
            prefixIcon ? "pl-[3.75rem]" : "",
            className
          )}
          {...rest}
        />

        {icon && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            {icon}
          </div>
        )}
      </div>
      <FormHelperText
        error={errorMessage}
        errorClassName={formStyles.errorText}
      />
    </div>
  );
};

export default Input;
